// Anti-perte audio phase 1, tâche A6 (spec §5.2.4) : repère les groupes de
// fichiers audio trouvés par le natif (`listTakes`, prises + fichiers en vrac
// des anciennes versions) qui n'ont pas d'équivalent complet côté serveur —
// « Enregistrement à terminer » (A9 l'affiche). Fonction PURE : aucun accès
// disque ni réseau ici ; la liste des captures et la trace des enregistrements
// en attente sont déjà chargées par l'appelant.
//
// `recordedAt` d'une capture = l'HEURE DE DÉBUT de la prise, pas la fin
// (`buildCapture` copie `startedAt.toISOString()` directement, voir
// `build-capture.ts:32`) : la comparaison se fait donc contre `startedAt` du
// groupe tel quel, sans ajouter `totalMs`.
import type { LegacyGroupInfo } from "./take-types";

// Fenêtre au-delà de laquelle un vieux FICHIER EN VRAC (racine, anciennes
// versions) n'est plus proposé. L'audio existe toujours sur le téléphone (rien
// n'est supprimé par cette fonction, I1) : c'est la copie SERVEUR qui est
// purgée à 14 jours ; passé ce délai, compléter une capture n'a plus de sens.
// Ne s'applique PAS aux dossiers de prise (`Takes/`, convention native
// introduite avec ce chantier) : un dossier resurgi du natif reste visible
// quel que soit son âge, c'est `listTakes` qui décide de sa présence.
export const ORPHAN_WINDOW_MS = 14 * 86_400_000;
// Sous ce seuil, un groupe de fichiers EN VRAC n'est pas une prise ratée
// (bruit). Même exemption pour les dossiers de prise.
export const ORPHAN_MIN_MS = 60_000;
/** Fenêtre de recherche autour de `startedAt` pour apparier une capture serveur. */
export const MATCH_START_TOLERANCE_MS = 60_000;
// Un segment de rotation dure ~30 s (spec §5.2.4) ; au-delà de 31 s, un groupe
// d'UN SEUL fichier en vrac est le résultat d'une FUSION (l'ancien
// `take.m4a` complet des versions antérieures à ce chantier), pas un segment
// isolé — voir `isDeletedMergeFile` ci-dessous.
const MERGED_FILE_MIN_MS = 31_000;

export interface ServerCaptureLite {
  id: string;
  recordedAt: string;
  durationS: number | null;
  status: string;
}

export type ToFinish =
  | { kind: "new"; groupId: string; startedAt: string; totalMs: number }
  | {
      kind: "complete";
      groupId: string;
      captureId: string;
      startedAt: string;
      totalMs: number;
      capturedMs: number;
      alreadyAnalyzed: boolean;
    };

// Statuts serveur qui signifient qu'une analyse a déjà été demandée (ou
// tentée puis échouée). R66 : la publication (`recordings-to-finish.ts`) ne
// propose alors rien — compléter l'audio ne relancerait pas l'analyse.
const ANALYZED_STATUSES = new Set(["transcribing", "processing", "ready", "error"]);

// Statuts où `durationS` ne reflète PAS encore une durée audio confirmée par
// le serveur, et ne doit donc jamais servir à conclure « déjà complète » :
// - `assembling`/`assembly_failed` : `duration_s` est la durée ATTENDUE posée
//   à la création de la ligne (`segment-upload.ts` `buildAssemblingRow`) ; le
//   mode "complete" (capture tronquée déjà en base) ne la touche même pas —
//   elle peut donc rester à son ancienne valeur (30 s, cas du 24-09) même une
//   fois l'assemblage retenté et retombé en échec.
// - `uploading`/`recording` : la capture est encore en cours de constitution.
// Sans cette garde, un dossier de prise au manifeste illisible qui matcherait
// par coïncidence une de ces lignes serait classé « déjà complet », ignoré —
// puis, passé `ORPHAN_WINDOW_MS`, perdu pour de bon (I1).
const NEVER_ALREADY_COMPLETE_STATUSES = new Set([
  "assembling",
  "assembly_failed",
  "uploading",
  "recording",
]);

/** La capture du serveur dont `recordedAt` est le plus proche de `startedAt`,
 * parmi celles dans la fenêtre ± `MATCH_START_TOLERANCE_MS` ; `null` sinon. */
function findClosestMatch(
  startedAtMs: number,
  captures: ServerCaptureLite[],
): ServerCaptureLite | null {
  let best: ServerCaptureLite | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const c of captures) {
    const delta = Math.abs(new Date(c.recordedAt).getTime() - startedAtMs);
    if (delta <= MATCH_START_TOLERANCE_MS && delta < bestDelta) {
      best = c;
      bestDelta = delta;
    }
  }
  return best;
}

// Bruit des fichiers EN VRAC (anciennes versions) : trop court, ou trop
// vieux pour que la copie serveur (purgée à 14 jours) existe encore. Un
// dossier de prise (`Takes/`) n'est jamais du bruit : `listTakes` décide déjà
// de sa présence, quel que soit son âge ou sa durée (idée flash courte,
// manifeste illisible avec `totalMs = 0`, etc.).
function isNoiseGroup(
  g: LegacyGroupInfo,
  isTakeFolder: boolean,
  startedAtMs: number,
  nowMs: number,
): boolean {
  if (isTakeFolder) return false;
  if (g.totalMs < ORPHAN_MIN_MS) return true;
  // `startedAtMs` NaN (date illisible) : la comparaison est fausse → jamais
  // considéré trop vieux, le groupe reste candidat (retombe en `new` plus
  // loin, faute d'appariement possible).
  return nowMs - startedAtMs > ORPHAN_WINDOW_MS;
}

// Décision du contrôleur (revue A6, R48) : les anciennes versions laissaient
// à la racine le fichier FUSIONNÉ d'une capture déjà envoyée, même après
// suppression de la capture par la personne. Un tel fichier (groupe en vrac
// d'UN SEUL fichier, > 31 s : une fusion, pas un segment de rotation) sans
// capture correspondante n'est reproposé que s'il figure dans la trace des
// enregistrements en attente jamais rattachés (`pending-recording.ts`,
// incident du 2026-08-17) — sinon on proposerait de renvoyer un
// enregistrement supprimé (Loi 25).
function isDeletedMergeFile(
  g: LegacyGroupInfo,
  isTakeFolder: boolean,
  pendingFiles: ReadonlySet<string>,
): boolean {
  if (isTakeFolder || g.totalMs <= MERGED_FILE_MIN_MS) return false;
  const soloFile = g.files.length === 1 ? g.files[0] : undefined;
  return soloFile !== undefined && !pendingFiles.has(soloFile);
}

export function findRecordingsToFinish(
  groups: LegacyGroupInfo[],
  captures: ServerCaptureLite[],
  pendingFiles: ReadonlySet<string>,
  nowMs: number,
): ToFinish[] {
  const result: ToFinish[] = [];
  for (const g of groups) {
    const isTakeFolder = g.groupId.startsWith("Takes/");
    const startedAtMs = new Date(g.startedAt).getTime();
    if (isNoiseGroup(g, isTakeFolder, startedAtMs, nowMs)) continue;

    const match = findClosestMatch(startedAtMs, captures);

    if (!match) {
      if (isDeletedMergeFile(g, isTakeFolder, pendingFiles)) continue;
      result.push({ kind: "new", groupId: g.groupId, startedAt: g.startedAt, totalMs: g.totalMs });
      continue;
    }

    // `durationS` non confirmée (null) : jamais considérée « déjà complète ».
    const capturedMs = (match.durationS ?? 0) * 1000;
    const alreadyComplete =
      !NEVER_ALREADY_COMPLETE_STATUSES.has(match.status) && capturedMs >= g.totalMs - 2_000;
    if (alreadyComplete) continue;

    result.push({
      kind: "complete",
      groupId: g.groupId,
      captureId: match.id,
      startedAt: g.startedAt,
      totalMs: g.totalMs,
      capturedMs,
      alreadyAnalyzed: ANALYZED_STATUSES.has(match.status),
    });
  }
  return result;
}
